// CustomerInvoice.jsx - Professional customer-facing repair order invoice
// REBUILT FROM SCRATCH - Simple, clean, guaranteed to work
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import VinDisplay from '../shared/VinDisplay';

const SHOP_INFO = {
  name: 'THE WHOLESALE SERVICE CENTER',
  address: '4015 W. ALEXIS RD.',
  city: 'TOLEDO, OHIO 43623'
};

const TAX_RATE = 0.0775; // 7.75%
const SHOP_SUPPLIES_RATE = 0.08; // 8% of labor - industry standard
const HAZMAT_RATE = 0.02; // 2% of parts - industry standard

const CustomerInvoice = ({ roId, onClose, onSave }) => {
  const [ro, setRO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [discount, setDiscount] = useState({ type: 'flat', value: 0 });
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [laborRate, setLaborRate] = useState(95); // Industry standard labor rate
  const [shopSuppliesEnabled, setShopSuppliesEnabled] = useState(true);
  const [hazmatEnabled, setHazmatEnabled] = useState(true);
  const [customerNotes, setCustomerNotes] = useState('');
  const [wheelsRemoved, setWheelsRemoved] = useState(false);

  // Load RO data on mount
  useEffect(() => {
    const fetchRO = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('CustomerInvoice: Fetching RO', roId);
        const data = await api.getRepairOrder(roId);
        console.log('CustomerInvoice: RO data received', data);
        setRO(data);
        if (data.discountAmount) {
          setDiscount({ type: 'flat', value: parseFloat(data.discountAmount) || 0 });
        }
        if (data.taxEnabled !== undefined) {
          setTaxEnabled(data.taxEnabled);
        }
        if (data.laborRate) {
          setLaborRate(parseFloat(data.laborRate) || 85);
        }
      } catch (err) {
        console.error('CustomerInvoice: Failed to load RO:', err);
        setError(err.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };
    fetchRO();
  }, [roId]);

  // Calculate totals with shop supplies and hazmat
  const calculateTotals = () => {
    if (!ro) return { parts: 0, labor: 0, shopSupplies: 0, hazmat: 0, subtotal: 0, discountAmount: 0, taxAmount: 0, total: 0 };
    
    // Parts total
    const partsTotal = (ro.parts || []).reduce((sum, p) => {
      const qty = parseInt(p.quantity) || 1;
      const cost = parseFloat(p.unitCost) || 0;
      return sum + (qty * cost);
    }, 0);
    
    // Labor total
    const laborHours = parseFloat(ro.laborHours) || 0;
    const laborTotal = laborHours * laborRate;
    
    // Shop Supplies (8% of labor - industry standard)
    const shopSupplies = shopSuppliesEnabled ? laborTotal * SHOP_SUPPLIES_RATE : 0;
    
    // Hazmat/Environmental (2% of parts - industry standard)
    const hazmat = hazmatEnabled ? partsTotal * HAZMAT_RATE : 0;
    
    // Subtotal (parts + labor + shop supplies + hazmat)
    const subtotal = partsTotal + laborTotal + shopSupplies + hazmat;
    
    // Discount
    let discountAmount = 0;
    if (discount.type === 'flat') {
      discountAmount = parseFloat(discount.value) || 0;
    } else if (discount.type === 'percent') {
      discountAmount = subtotal * ((parseFloat(discount.value) || 0) / 100);
    }
    
    // After discount
    const afterDiscount = subtotal - discountAmount;
    
    // Tax (on parts and labor, not on shop supplies per Ohio law)
    const taxableAmount = taxEnabled ? (partsTotal + laborTotal - discountAmount) : 0;
    const taxAmount = taxableAmount > 0 ? taxableAmount * TAX_RATE : 0;
    
    // Grand total
    const total = afterDiscount + taxAmount;
    
    return { parts: partsTotal, labor: laborTotal, shopSupplies, hazmat, subtotal, discountAmount, taxAmount, total };
  };

  const totals = calculateTotals();

  // Save invoice settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateRepairOrder(roId, {
        discountAmount: discount.type === 'flat' ? discount.value : totals.discountAmount,
        taxEnabled,
        laborRate
      });
      if (onSave) onSave();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Print invoice and notify accounting
  const handlePrint = async () => {
    window.print();
    // Create accounting notification when invoice is printed
    try {
      await api.createNotification({
        type: 'customer_invoice',
        title: 'Customer Invoice Printed',
        message: `Invoice ${ro.roNumber} for ${ro.customerName || 'Customer'} - $${totals.total.toFixed(2)} ready for accounting entry`,
        targetRole: 'admin',
        metadata: { roId, roNumber: ro.roNumber, total: totals.total }
      });
    } catch (err) {
      console.log('Could not create accounting notification:', err);
    }
  };

  // LOADING STATE
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="text-lg mb-4">Loading Invoice...</div>
          <button onClick={onClose} className="text-blue-600 hover:underline">Cancel</button>
        </div>
      </div>
    );
  }

  // ERROR STATE
  if (error || !ro) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="text-red-500 text-lg mb-4">{error || 'Failed to load invoice'}</div>
          <button onClick={onClose} className="px-4 py-2 bg-neutral-800 text-white rounded">Go Back</button>
        </div>
      </div>
    );
  }

  // Get labor hours
  const laborHours = parseFloat(ro.laborHours) || 0;

  // MAIN INVOICE VIEW - Professional Mitchell1-style layout
  return (
    <div className="min-h-screen bg-gray-200 py-6 print:bg-white print:py-0">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-invoice, .print-invoice * { visibility: visible; }
          .print-invoice { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto bg-white shadow-xl print:shadow-none">
        <div className="print-invoice">
          
          {/* Header - Professional Shop Info */}
          <div className="border-b-2 border-black px-8 py-5">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{SHOP_INFO.name}</h1>
                <p className="text-sm text-gray-600">{SHOP_INFO.address} • {SHOP_INFO.city}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">INVOICE</div>
                <div className="text-sm text-gray-600 mt-1">{new Date().toLocaleDateString()}</div>
              </div>
            </div>
          </div>

          {/* Invoice Info & Customer */}
          <div className="px-8 py-4 border-b border-gray-200">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Bill To</div>
                <div className="text-lg font-semibold">{ro.customerName || 'Walk-in Customer'}</div>
                {ro.customerPhone && <div className="text-sm text-gray-600">{ro.customerPhone}</div>}
                {ro.customerEmail && <div className="text-sm text-gray-600">{ro.customerEmail}</div>}
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Invoice Details</div>
                <div className="text-sm"><span className="text-gray-500">Invoice #:</span> <span className="font-mono font-semibold">{ro.roNumber}</span></div>
                <div className="text-sm"><span className="text-gray-500">Date:</span> {new Date(ro.createdAt).toLocaleDateString()}</div>
                <div className="text-sm"><span className="text-gray-500">Due:</span> Upon Receipt</div>
              </div>
            </div>
          </div>

          {/* Vehicle Information - Detailed */}
          <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Vehicle Information</div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-500 text-xs">Year / Make / Model</div>
                <div className="font-semibold">
                  {ro.customerVehicleYear || ro.vehicleYear} {ro.customerVehicleMake || ro.vehicleMake} {ro.customerVehicleModel || ro.vehicleModel}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Color</div>
                <div className="font-semibold">{ro.vehicleColor || ro.color || 'N/A'}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">VIN</div>
                <VinDisplay vin={ro.customerVehicleVin || ro.vin} />
              </div>
              <div>
                <div className="text-gray-500 text-xs">Odometer</div>
                <div className="font-semibold">{(ro.customerVehicleMileage || ro.mileage)?.toLocaleString() || 'N/A'} mi</div>
              </div>
            </div>
            {ro.licensePlate && (
              <div className="mt-2 text-sm">
                <span className="text-gray-500">License Plate:</span> <span className="font-semibold">{ro.licensePlate}</span>
              </div>
            )}
          </div>

          {/* Line Items Table - Mitchell1 Style */}
          <div className="px-8 py-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-400 bg-gray-100">
                  <th className="text-left py-2 px-2 font-semibold">Line Description</th>
                  <th className="text-left py-2 px-2 font-semibold w-24">Part No.</th>
                  <th className="text-left py-2 px-2 font-semibold w-24">Category</th>
                  <th className="text-right py-2 px-2 font-semibold w-12">Qty</th>
                  <th className="text-right py-2 px-2 font-semibold w-20">Sale</th>
                  <th className="text-right py-2 px-2 font-semibold w-20">Ext'd</th>
                </tr>
              </thead>
              <tbody>
                {/* Labor Lines */}
                {ro.workToBeDone && (
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2">{ro.workToBeDone}</td>
                    <td className="py-2 px-2 text-gray-500">-</td>
                    <td className="py-2 px-2 text-gray-600">Labor</td>
                    <td className="py-2 px-2 text-right">{laborHours}</td>
                    <td className="py-2 px-2 text-right">${laborRate.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right font-medium">${totals.labor.toFixed(2)}</td>
                  </tr>
                )}

                {/* Parts Lines */}
                {(ro.parts || []).map((part, idx) => {
                  const qty = parseInt(part.quantity) || 1;
                  const price = parseFloat(part.unitCost) || 0;
                  const amount = qty * price;
                  return (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="py-2 px-2">{part.description}</td>
                      <td className="py-2 px-2 font-mono text-xs">{part.partNumber || '-'}</td>
                      <td className="py-2 px-2 text-gray-600">Parts</td>
                      <td className="py-2 px-2 text-right">{qty}</td>
                      <td className="py-2 px-2 text-right">${price.toFixed(2)}</td>
                      <td className="py-2 px-2 text-right font-medium">${amount.toFixed(2)}</td>
                    </tr>
                  );
                })}

                {/* Shop Supplies Line */}
                {shopSuppliesEnabled && totals.shopSupplies > 0 && (
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="py-2 px-2">Shop Supplies (8% of labor)</td>
                    <td className="py-2 px-2 text-gray-500">-</td>
                    <td className="py-2 px-2 text-gray-600">Supplies</td>
                    <td className="py-2 px-2 text-right">1</td>
                    <td className="py-2 px-2 text-right">${totals.shopSupplies.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right font-medium">${totals.shopSupplies.toFixed(2)}</td>
                  </tr>
                )}

                {/* Hazmat/Environmental Line */}
                {hazmatEnabled && totals.hazmat > 0 && (
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="py-2 px-2">Hazmat/Environmental Fee (2% of parts)</td>
                    <td className="py-2 px-2 text-gray-500">-</td>
                    <td className="py-2 px-2 text-gray-600">Haz Mat</td>
                    <td className="py-2 px-2 text-right">1</td>
                    <td className="py-2 px-2 text-right">${totals.hazmat.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right font-medium">${totals.hazmat.toFixed(2)}</td>
                  </tr>
                )}

                {/* Empty state */}
                {!ro.workToBeDone && (!ro.parts || ro.parts.length === 0) && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400 italic">No line items</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals Section - Mitchell1 Style */}
          <div className="px-8 py-4 border-t-2 border-gray-400">
            <div className="flex justify-end">
              <table className="text-sm w-80">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-1 font-semibold">Parts:</td>
                    <td className="py-1 text-right font-mono">${totals.parts.toFixed(2)}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1 font-semibold">Labor:</td>
                    <td className="py-1 text-right font-mono">${totals.labor.toFixed(2)}</td>
                  </tr>
                  {shopSuppliesEnabled && totals.shopSupplies > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-1 font-semibold">Supplies:</td>
                      <td className="py-1 text-right font-mono">${totals.shopSupplies.toFixed(2)}</td>
                    </tr>
                  )}
                  {hazmatEnabled && totals.hazmat > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-1 font-semibold">Haz Mat:</td>
                      <td className="py-1 text-right font-mono">${totals.hazmat.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="border-b border-gray-200">
                    <td className="py-1 font-semibold">Subtotal:</td>
                    <td className="py-1 text-right font-mono">${totals.subtotal.toFixed(2)}</td>
                  </tr>
                  {totals.discountAmount > 0 && (
                    <tr className="border-b border-gray-200 text-green-600">
                      <td className="py-1 font-semibold">Discount:</td>
                      <td className="py-1 text-right font-mono">-${totals.discountAmount.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="border-b border-gray-200">
                    <td className="py-1 font-semibold">Tax:</td>
                    <td className="py-1 text-right font-mono">${totals.taxAmount.toFixed(2)}</td>
                  </tr>
                  <tr className="bg-gray-100 font-bold text-lg">
                    <td className="py-2 px-2">Total:</td>
                    <td className="py-2 px-2 text-right font-mono">${totals.total.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Customer Notes Section */}
          {customerNotes && (
            <div className="px-8 py-4 border-t border-gray-200">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Notes</div>
              <div className="text-sm whitespace-pre-wrap">{customerNotes}</div>
            </div>
          )}

          {/* Retorque Warning - Only shows if wheels were removed */}
          {wheelsRemoved && (
            <div className="px-8 py-3 bg-yellow-50 border-t border-yellow-200">
              <div className="flex items-center gap-2 text-yellow-800">
                <span className="text-lg">⚠️</span>
                <span className="font-semibold text-sm">IMPORTANT: Wheels were removed during service. Please retorque lug nuts after 50 miles.</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-8 py-6 border-t-2 border-gray-300 bg-gray-50">
            <div className="text-center">
              <p className="text-sm font-medium">All work guaranteed. Payment due upon completion.</p>
              <p className="text-xs text-gray-500 mt-2">Thank you for choosing {SHOP_INFO.name}!</p>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-8 text-xs text-gray-500">
              <div>
                <div className="font-semibold text-gray-700 mb-1">Customer Signature</div>
                <div className="border-b border-gray-400 h-8"></div>
              </div>
              <div>
                <div className="font-semibold text-gray-700 mb-1">Date</div>
                <div className="border-b border-gray-400 h-8"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - No Print */}
        <div className="px-8 py-4 border-t-2 border-gray-300 bg-gray-100 no-print">
          {/* Top row - Notes input */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Customer Notes (will print on invoice)</label>
            <textarea
              value={customerNotes}
              onChange={e => setCustomerNotes(e.target.value)}
              placeholder="Enter any notes for the customer..."
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
              rows={2}
            />
          </div>

          {/* Bottom row - Options and buttons */}
          <div className="flex justify-between items-center">
            <button 
              onClick={onClose} 
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              ← Back to RO
            </button>
            
            {/* Editable Options */}
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wheelsRemoved}
                  onChange={e => setWheelsRemoved(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Wheels Removed</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shopSuppliesEnabled}
                  onChange={e => setShopSuppliesEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Shop Supplies</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hazmatEnabled}
                  onChange={e => setHazmatEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Hazmat</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={taxEnabled}
                  onChange={e => setTaxEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Tax</span>
              </label>
              <div className="flex items-center gap-1 border-l border-gray-300 pl-4">
                <span>Discount:</span>
                <select 
                  value={discount.type} 
                  onChange={e => setDiscount(d => ({ ...d, type: e.target.value }))}
                  className="text-xs border border-gray-300 rounded px-1 py-1"
                >
                  <option value="flat">$</option>
                  <option value="percent">%</option>
                </select>
                <input
                  type="number"
                  value={discount.value}
                  onChange={e => setDiscount(d => ({ ...d, value: e.target.value }))}
                  className="w-16 text-right text-sm border border-gray-300 rounded px-2 py-1"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button 
                onClick={handlePrint}
                className="px-6 py-2 bg-gray-800 text-white rounded hover:bg-gray-900 font-medium flex items-center gap-2"
              >
                🖨️ Print Invoice
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInvoice;
